import * as Sentry from "@sentry/react";
import React, { useEffect, useRef } from "react";

import { PageError } from "../components/Error";

import { useCompanySettings, useCurrentUser } from "../api/hooks";
import { useFeatureFlagQuery } from "../hooks/useFeatureFlag";

import beforeSendEventFunction from "../utils/logging";

import SentryConfig from "../constants/Sentry";

const SentryErrorWrapper = ({ children }: { children: React.ReactNode }) => {
	const initialized = useRef(false);
	const replayRef = useRef<any>(null);
	const { hostname } = window.location;

	const { data: user } = useCurrentUser();
	const { data: company } = useCompanySettings();
	const { value: isSessionReplaysEnabled } = useFeatureFlagQuery("session-replays", false);

	useEffect(() => {
		if (initialized.current) return;

		// dont track in local environment
		if (hostname === "localhost") return;

		initialized.current = true;

		const replay = Sentry.replayIntegration({
			networkDetailDenyUrls: ["fonts.googleapis.com", "cdn.performyard.com"],
			networkDetailAllowUrls: [window.location.origin, /^https?:\/\/.*\.performyard\.com/],
			networkRequestHeaders: [
				"accept",
				"accept-encoding",
				"accept-language",
				"connection",
				"host",
				"content-type",
				"user-agent",
				"cache-control",
			],
			networkResponseHeaders: [
				"access-control-allow-headers",
				"access-control-allow-origin",
				"cache-control",
				"content-type",
			],
		});

		// Store replay reference for later use to avoid creating multiple instances
		replayRef.current = replay;

		Sentry.init({
			dsn: SentryConfig.APIKey,
			environment: hostname,

			// Drop errors thrown by third-party scripts we don't ship or control.
			// VisitorAPI (https://cdn.visitorapi.com/visitor-api.js) gets injected
			// into some users' pages externally and throws "Unexpected end of JSON
			// input" on every load, which was consuming the entire Sentry quota
			// within ~1 week each month. denyUrls matches the stack frame's source
			// file URL. See PY-12990.
			denyUrls: [/visitorapi\.com/i],

			// If the flag is enabled, record everything. And don't record
			// anything if the flag is disabled.
			replaysSessionSampleRate: isSessionReplaysEnabled ? 1.0 : 0.0,

			// Also disable session recording if the flag is disabled
			replaysOnErrorSampleRate: isSessionReplaysEnabled ? 1.0 : 0.0,

			// To set your release version
			release: (window as any)?.PYENVIRONMENT?.release || "unknown",
			integrations: [
				Sentry.browserTracingIntegration(),
				replay,

				// Rewrite Frames is intended to replace old config option includePaths
				// For documentation on Rewrite Frames, see https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/plugin/
				// And also https://github.com/getsentry/sentry-javascript/issues/1631
				Sentry.rewriteFramesIntegration({
					iteratee: (frame: any) => {
						if (frame.filename) {
							frame.in_cdn = /https?:\/\/cdn\.performyard\.com/.test(frame.filename);
							frame.in_py = /https?:\/\/talent\.performyard\.com/.test(frame.filename);
							frame.in_app = frame.in_cdn | frame.in_py;
						}

						return frame;
					},
				}),
			],

			// Remove sensitive info before sending to Sentry
			beforeSend: beforeSendEventFunction,
		});
	}, []);

	// Update user/company context when they become available
	useEffect(() => {
		if (company) {
			Sentry.setContext("company", {
				company_name: company?.company?.name,
				company_id: company?.company?.id,
			});
			Sentry.setTag("company_id", company?.company?.id);
		}
	}, [company]);

	useEffect(() => {
		if (user) {
			Sentry.setUser({
				id: user?.me?.authorization?.payload?.user_id,
				email: user?.me?.email,
				role: user?.me?.authorization?.payload?.user_primary_role,
			});
		}
	}, [user]);

	useEffect(() => {
		// This ensures the session is recorded. It would sometimes not record
		// even though the sample rate is 1.0 :shrug:
		// Use the existing replay instance instead of creating a new one
		if (isSessionReplaysEnabled && replayRef.current) {
			replayRef.current.start();
		}
	}, [isSessionReplaysEnabled]);

	return <Sentry.ErrorBoundary fallback={<PageError />}>{children}</Sentry.ErrorBoundary>;
};

export default SentryErrorWrapper;
